import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

/**
 * Create an axios instance
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Add an interceptor to include the access token in all requests
 */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Login user
 * @param {string} username 
 * @param {string} password 
 */
export const loginUser = async (contact, password) => {
  try {
    const response = await api.post('/user/login', {
      contact,
      password,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Login failed');
  }
};

/**
 * Fetch all missions
 */
export const fetchAllMissions = async (params = {}) => {
  const { page = 0, limit = 50, ...rest } = params;
  const offset = page * limit;
  try {
    const response = await api.get(`/mission/findAllMissions/${offset}/${limit}`, { params: rest });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch missions');
  }
};

/**
 * Fetch active duties
 */
export const fetchAllDuties = async (params = {}) => {
  const { page = 0, limit = 50, ...rest } = params;
  const offset = page * limit;
  try {
    const response = await api.get(`/duty/findAllDuties/${offset}/${limit}`, { params: rest });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch active duties');
  }
};

/**
 * Fetch QC missions
 */

export const fetchAllQcMissions = async (params = {}) => {
  try {
    const { page = 0, limit = 50, ...rest } = params;
    const offset = page * limit;
    const response = await api.get(`/qc/findAllQcMissions/${offset}/${limit}`, { params: rest });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch QC missions');
  }
}

/**
 * Fetch Active duties
 */
export const fetchActiveDuties = async () => {
  try {
    const response = await api.get(`/duty/findActiveDuties`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch active duties');
  }
}

/**
 * Fetch distinct cities for filtering
 */
export const fetchCities = async () => {
  try {
    const response = await api.get('/collectors/getCities');
    // console.log(response.data);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      console.error('Unauthorized access to /collectors/getCities. Please check if the current user (Admin) has permission to access this endpoint.');
    } else {
      console.warn('Failed to fetch cities:', error);
    }
    return { data: [] }; // Return empty list on failure to not break UI
  }
};

/**
 * Fetch Mission Logs with collector duties and missions
 */
export const fetchMissionLogs = async () => {
  try {
    // Get collector ID from stored profile
    const storedProfile = localStorage.getItem('collectors_data');
    if (!storedProfile) {
      throw new Error('No user profile found');
    }

    const profile = JSON.parse(storedProfile);
    const collectorsId = profile.collectorsId;

    if (!collectorsId) {
      throw new Error('No collectors ID found');
    }

    // Use duty endpoint which returns duties with missions
    const response = await api.get(`/duty/findDutyByCollector/${collectorsId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch mission logs');
  }
};

/**
 * Fetch active duty by collector ID
 * @param {string} collectorsId
 */
export const fetchActiveDuty = async (collectorsId) => {
  try {
    const response = await api.get(`/duty/activeDuty/${collectorsId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch active duty');
  }
};

/**
 * Fetch logged-in collector profile from DB and update localStorage
 */
export const fetchCollectorProfile = async () => {
  try {
    // Get collectorsId from localStorage
    const storedProfile = localStorage.getItem('collectors_data');
    if (!storedProfile) {
      throw new Error('No user profile found in localStorage');
    }

    const profile = JSON.parse(storedProfile);
    const collectorsId = profile.collectorsId;

    if (!collectorsId) {
      throw new Error('No collectors ID found');
    }

    const response = await api.get(`/collectors/findCollector/${collectorsId}`);

    // Update local storage with fresh data
    if (response.data && response.data.data) {
      const updatedProfile = { ...profile, ...response.data.data };
      localStorage.setItem('collectors_data', JSON.stringify(updatedProfile));
    }

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user profile');
  }
};



/**
 * Create a new duty (Start Mission)
 * @param {Object} dutyData - Duty creation data
 * @param {string} dutyData.username - Collector username
 * @param {string} dutyData.dsid - DSID (optional)
 * @param {string} dutyData.selfie - Base64 image string
 * @param {string} dutyData.start_time - Start timestamp
 * @param {string} dutyData.stop_time - Stop timestamp (optional)
 * @param {string} dutyData.collectorsId - Collector ID
 */
export const createDuty = async (dutyData) => {
  try {
    // Convert base64 to blob for file upload if needed
    const response = await api.post('/duty/createDuty', dutyData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create duty');
  }
};

/**
 * Update duty (e.g., set stop time when mission ends)
 * @param {string} dutyId - Duty ID
 * @param {Object} updateData - Data to update (e.g., stop_time)
 */
export const updateDuty = async (dutyId, updateData) => {
  try {
    const response = await api.put(`/duty/updateDuty/${dutyId}`, updateData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update duty');
  }
};

export default api;

/**
 * Create a new mission
 * @param {Object} missionData - Mission data
 * @param {string} missionData.missionId - Mission ID
 * @param {string} missionData.dutyId - Duty ID
 * @param {string} missionData.valid - POI Visible count
 * @param {string} missionData.invalid - POI Not Visible count
 * @param {string} missionData.created - POI Added count
 * @param {string} missionData.status - Mission status
 */
export const createMission = async (missionData) => {
  try {
    const response = await api.post('/mission/createMission', {
      ...missionData,
      closed: null  // Always null when creating
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create mission');
  }
};

/**
 * Update an existing mission (e.g., set stop time)
 * @param {string} id - Mission ID
 * @param {Object} updateData - Data to update
 */
export const updateMission = async (id, updateData) => {
  try {
    const response = await api.put(`/mission/updateMission/${id}`, updateData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update mission');
  }
};

export const createQc = async (missionId, dutyId) => {
  try {
    const response = await api.post('/qc/createQc', {
      dutyId: dutyId,
      missionId: missionId
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create QC form');
  }
};

export const logout = async () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('username');
  localStorage.removeItem('collectors_data');
  localStorage.removeItem('active_duty_id');
  localStorage.removeItem('mission_status');
  if (localStorage.getItem('qc_pending')) {
    localStorage.removeItem('qc_pending');
  }
  window.location.href = '/';
}